import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteFoodHistoryMutation,
  useGetFoodHistoryQuery
} from '../../../../../services/endpoints/content/foodHistories';
import { FoodHistory } from '../../../../../types/content.type';
import { getErrorMessage } from '../../../../../utils/utils';
import SafeHTMLDisplay from '../../../../common/SafeHTMLDisplay';

export const FoodHistoryDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetFoodHistoryQuery(+params.id!);
  const [deleteFoodHistory, { isLoading: isDeleting }] = useDeleteFoodHistoryMutation();
  const [foodHistory, setFoodHistory] = useState<FoodHistory>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading food history', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data?.foodHistory) {
      setFoodHistory(data.foodHistory);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteFoodHistory(+params.id!).unwrap();
      addToast('Food history deleted successfully', 'success');
      navigate('/admin/content/food-histories');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !foodHistory) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/content/food-histories')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Food History List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`FoodHistory #${foodHistory?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/content/FoodHistorys/${foodHistory?.id}/edit`)}
          >
            Edit Food History
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Food History
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Id:"
              text={foodHistory?.id}
            />
            <LabelText
              label="Label:"
              text={foodHistory?.name}
            />
            <LabelText label="Content:">
              <SafeHTMLDisplay htmlContent={foodHistory?.content} />
            </LabelText>
          </DetailSection>
        </Stack>
      </div>

      <ConfirmationModal
        show={showModal}
        title="Delete Food History Plan"
        message="Are you sure you want to remove this FoodHistory?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
